import { INTERNAL_SCHEMA_VERSION, createEntityMetadata } from "./common";
import type {
  BehaviorCorrection,
  EvidenceStatement,
  FocusDeviationRecord,
  IdeaParkingItem,
  NoStartItem,
  PrivateSourceRecord,
  PromiseEntry,
  PromiseMateriality,
  CounterpartyType,
} from "./types";

export function createPrivateSourceRecord(
  rawText: string,
  options: {
    recordType?: PrivateSourceRecord["recordType"];
    sourceMode?: PrivateSourceRecord["sourceMode"];
    sensitivity?: PrivateSourceRecord["sensitivity"];
    retentionPolicy?: PrivateSourceRecord["retentionPolicy"];
    occurredAt?: string;
    now?: string;
    id?: string;
  } = {},
): PrivateSourceRecord {
  const metadata = createEntityMetadata(
    { kind: "user_input", referenceIds: [] },
    {
      ...(options.id === undefined ? {} : { id: options.id }),
      ...(options.now === undefined ? {} : { now: options.now }),
      status: "active",
      exportAllowed: false,
      deletionPolicy: "hard_on_request",
    },
  );
  return {
    ...metadata,
    recordType: options.recordType ?? "other",
    occurredAt: options.occurredAt ?? metadata.createdAt,
    rawText,
    sourceMode: options.sourceMode ?? "manual",
    sensitivity: options.sensitivity ?? "sensitive",
    retentionPolicy: options.retentionPolicy ?? "keep_until_deleted",
    exportAllowed: false,
  };
}

export function createIdeaParkingItem(
  title: string,
  effectiveDate: string,
  coolingUntil: string,
  now = new Date().toISOString(),
  options: {
    description?: string;
    captureSource?: IdeaParkingItem["captureSource"];
    sourceRecordIds?: string[];
    relatedMainline?: string;
    relatedConstraint?: string;
    expectedValue?: string;
    urgencyClaim?: string;
    resourceCost?: string;
    opportunityCost?: string;
  } = {},
): IdeaParkingItem {
  return {
    ...createEntityMetadata(
      { kind: "user_input", referenceIds: options.sourceRecordIds ?? [] },
      { now, status: "active", exportAllowed: false },
    ),
    recordType: "idea_parking",
    effectiveDate,
    sourceRecordIds: options.sourceRecordIds ?? [],
    userConfirmation: "confirmed",
    title,
    ...(options.description === undefined ? {} : { description: options.description }),
    capturedAt: now,
    captureSource: options.captureSource ?? "manual",
    relatedMainline: options.relatedMainline ?? "",
    relatedConstraint: options.relatedConstraint ?? "",
    expectedValue: options.expectedValue ?? "",
    urgencyClaim: options.urgencyClaim ?? "",
    resourceCost: options.resourceCost ?? "",
    opportunityCost: options.opportunityCost ?? "",
    coolingUntil,
    parkingStatus: "parked",
  };
}

export function createEvidenceStatement(
  input: {
    type: EvidenceStatement["statementType"];
    content: string;
    effectiveDate: string;
    generatedBy: "user" | "local_rule" | "ai";
    evidenceRefs?: string[];
    sourceRecordIds?: string[];
    userConfirmed?: boolean;
    confidence?: number;
    supportingFactIds?: string[];
    alternativeExplanation?: string;
    verificationCondition?: string;
    falsificationCondition?: string;
    basedOnStatementIds?: string[];
    principleIds?: string[];
    supportsStatementIds?: string[];
    contradictsStatementIds?: string[];
    sensitivity?: EvidenceStatement["sensitivity"];
    truthInboxStatus?: EvidenceStatement["truthInboxStatus"];
    reviewDueAt?: string;
    supersedesStatementId?: string;
    id?: string;
  },
  now = new Date().toISOString(),
): EvidenceStatement {
  const base = {
    ...createEntityMetadata(
      {
        kind:
          input.generatedBy === "ai"
            ? ("ai_assisted" as const)
            : input.generatedBy === "local_rule"
              ? ("local_rule" as const)
              : ("user_input" as const),
        referenceIds: input.sourceRecordIds ?? [],
      },
      {
        ...(input.id === undefined ? {} : { id: input.id }),
        now,
        status: input.userConfirmed ? "active" : "draft",
        exportAllowed: false,
      },
    ),
    schemaVersion: INTERNAL_SCHEMA_VERSION,
    status: input.userConfirmed ? ("active" as const) : ("draft" as const),
    recordType: "evidence_statement" as const,
    effectiveDate: input.effectiveDate,
    sourceRecordIds: input.sourceRecordIds ?? [],
    userConfirmation: input.userConfirmed ? ("confirmed" as const) : ("unconfirmed" as const),
    ...(input.confidence === undefined ? {} : { confidence: input.confidence }),
    content: input.content,
    evidenceRefs: input.evidenceRefs ?? [],
    supportsStatementIds: input.supportsStatementIds ?? [],
    contradictsStatementIds: input.contradictsStatementIds ?? [],
    generatedBy: input.generatedBy,
    sensitivity: input.sensitivity ?? "standard",
    truthInboxStatus: input.truthInboxStatus ?? "none",
    ...(input.reviewDueAt === undefined ? {} : { reviewDueAt: input.reviewDueAt }),
    ...(input.supersedesStatementId === undefined
      ? {}
      : { supersedesStatementId: input.supersedesStatementId }),
  };

  switch (input.type) {
    case "fact":
      return { ...base, statementType: "fact" };
    case "inference":
      return {
        ...base,
        statementType: "inference",
        supportingFactIds: input.supportingFactIds ?? [],
        ...(input.alternativeExplanation === undefined
          ? {}
          : { alternativeExplanation: input.alternativeExplanation }),
      };
    case "hypothesis":
      return {
        ...base,
        statementType: "hypothesis",
        ...(input.verificationCondition === undefined
          ? {}
          : { verificationCondition: input.verificationCondition }),
        ...(input.falsificationCondition === undefined
          ? {}
          : { falsificationCondition: input.falsificationCondition }),
      };
    case "recommendation":
      return {
        ...base,
        statementType: "recommendation",
        basedOnStatementIds: input.basedOnStatementIds ?? [],
        principleIds: input.principleIds ?? [],
      };
  }
}

export function createBehaviorCorrection(
  input: {
    effectiveDate: string;
    behaviorToChange: string;
    action: string;
    affectedBusinessResult: string;
    deadline: string;
    verificationMetric: string;
    sourceReviewId: string;
    sourceFindingIds?: string[];
  },
  now = new Date().toISOString(),
): BehaviorCorrection {
  return {
    ...createEntityMetadata(
      { kind: "user_input", referenceIds: input.sourceFindingIds ?? [] },
      { now, status: "confirmed", exportAllowed: true },
    ),
    recordType: "behavior_correction",
    effectiveDate: input.effectiveDate,
    sourceRecordIds: [input.sourceReviewId],
    userConfirmation: "confirmed",
    behaviorToChange: input.behaviorToChange,
    action: input.action,
    owner: "founder",
    deadline: input.deadline,
    verificationMetric: input.verificationMetric,
    affectedBusinessResult: input.affectedBusinessResult,
    sourceReviewId: input.sourceReviewId,
    correctionStatus: "active",
    sourceFindingIds: input.sourceFindingIds ?? [],
  };
}

export function createNoStartItem(
  input: {
    title: string;
    reason: string;
    effectiveDate: string;
    origin: NoStartItem["origin"];
    sourceContextId?: string;
    activeUntil?: string;
    releaseCondition?: string;
    reviewDueAt?: string;
  },
  now = new Date().toISOString(),
): NoStartItem {
  const referenceIds = input.sourceContextId ? [input.sourceContextId] : [];
  return {
    ...createEntityMetadata(
      {
        kind: input.origin === "board_import" ? "board_import" : "user_input",
        referenceIds,
      },
      { now, status: "active", exportAllowed: false },
    ),
    recordType: "no_start",
    effectiveDate: input.effectiveDate,
    sourceRecordIds: referenceIds,
    userConfirmation: "confirmed",
    title: input.title,
    reason: input.reason,
    origin: input.origin,
    ...(input.sourceContextId === undefined ? {} : { sourceContextId: input.sourceContextId }),
    activeFrom: input.effectiveDate,
    ...(input.activeUntil === undefined ? {} : { activeUntil: input.activeUntil }),
    ...(input.releaseCondition === undefined ? {} : { releaseCondition: input.releaseCondition }),
    ...(input.reviewDueAt === undefined ? {} : { reviewDueAt: input.reviewDueAt }),
  };
}

export function createPromiseEntry(
  input: {
    effectiveDate: string;
    counterpartyType: CounterpartyType;
    counterpartyAlias: string;
    content: string;
    promisedAt: string;
    dueAt: string;
    businessTimezone: string;
    materiality: PromiseMateriality;
    trustImpact: string;
    promiseSource?: PromiseEntry["promiseSource"];
    confidentiality?: PromiseEntry["confidentiality"];
    sourceRecordIds?: string[];
  },
  now = new Date().toISOString(),
): PromiseEntry {
  const sourceRecordIds = input.sourceRecordIds ?? [];
  const id = crypto.randomUUID();
  return {
    ...createEntityMetadata(
      { kind: "user_input", referenceIds: sourceRecordIds },
      { id, now, status: "active", exportAllowed: false },
    ),
    recordType: "promise",
    effectiveDate: input.effectiveDate,
    sourceRecordIds,
    userConfirmation: "confirmed",
    counterpartyType: input.counterpartyType,
    counterpartyAlias: input.counterpartyAlias,
    content: input.content,
    promisedAt: input.promisedAt,
    dueAt: input.dueAt,
    originalDueAt: input.dueAt,
    businessTimezone: input.businessTimezone,
    materiality: input.materiality,
    trustImpact: input.trustImpact,
    promiseSource: input.promiseSource ?? "manual",
    promiseStatus: "active",
    confidentiality: input.confidentiality ?? "sensitive",
    history: [{ id: crypto.randomUUID(), action: "created", occurredAt: now }],
  };
}

export function createFocusDeviation(
  input: {
    effectiveDate: string;
    deviationContent: string;
    timeSpentMinutes?: number;
    relatedToNewIdea: boolean;
    affectedCriticalBusinessAction: boolean;
    repeated: boolean;
    tomorrowCorrection: string;
    sourceReviewId: string;
  },
  now = new Date().toISOString(),
): FocusDeviationRecord {
  return {
    ...createEntityMetadata(
      { kind: "user_input", referenceIds: [input.sourceReviewId] },
      { now, status: "active", exportAllowed: false },
    ),
    recordType: "focus_deviation",
    effectiveDate: input.effectiveDate,
    sourceRecordIds: [input.sourceReviewId],
    userConfirmation: "confirmed",
    deviationContent: input.deviationContent,
    ...(input.timeSpentMinutes === undefined ? {} : { timeSpentMinutes: input.timeSpentMinutes }),
    relatedToNewIdea: input.relatedToNewIdea,
    affectedCriticalBusinessAction: input.affectedCriticalBusinessAction,
    repeated: input.repeated,
    tomorrowCorrection: input.tomorrowCorrection,
    sourceReviewId: input.sourceReviewId,
  };
}
